import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import { Typography, List, ListItem, ListItemText } from '@mui/material';
import { InfoOutlined, FactCheckOutlined, SwitchAccessShortcutOutlined } from '@mui/icons-material';

const useStyles = makeStyles<Theme>((theme) => ({
  cardHeader: {
    textAlign: 'center',
    '& h4': {
      marginTop: '0',
      marginBottom: '0',
      fontSize: '1.5rem',
    },
  },
  cardBody: {
    padding: '20px',
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: theme.spacing(1),
      color:
        theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
    },
  },
  listItem: {
    paddingLeft: 0,
  },
  listItemIcon: {
    minWidth: '40px',
    color:
      theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    '& svg': {
      fontSize: '3rem',
      color:
        theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
    },
  },
  techStack: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(0.5),
    borderColor:
      theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
    '& .MuiChip-icon': {
      color:
        theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
    },
    '& .MuiChip-label': {
      color:
        theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
    },
  },
  bulletPoint: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
    marginRight: '12px',
  },
}));

export default function ToolsDevsContent() {
  const { t } = useTranslation();
  const classes = useStyles();

  const getTranslationArray = (key: string): string[] => {
    const result = t(key, { returnObjects: true });
    return Array.isArray(result) ? result : [];
  };

  const requirements = getTranslationArray('workWithUs.toolsDevs.requirements.items');
  const benefits = getTranslationArray('workWithUs.toolsDevs.benefits.items');

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardBody className={classes.cardBody}>
            <div className={classes.section}>
              <Typography variant="h5" className={classes.sectionTitle}>
                <InfoOutlined /> {t('workWithUs.about')}
              </Typography>
              <Typography variant="h6" gutterBottom>
                {t('workWithUs.toolsDevs.about.title')}
              </Typography>
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{ __html: t('workWithUs.toolsDevs.about.description') }}
              />
            </div>

            <div className={classes.section}>
              <Typography variant="h5" className={classes.sectionTitle}>
                <FactCheckOutlined /> {t('workWithUs.requirements')}
              </Typography>
              <List>
                {requirements.map((requirement: string, index: number) => (
                  <ListItem key={index} className={classes.listItem}>
                    <div className={classes.bulletPoint} />
                    <ListItemText primary={requirement} />
                  </ListItem>
                ))}
              </List>
            </div>

            <div className={classes.section}>
              <Typography variant="h5" className={classes.sectionTitle}>
                <SwitchAccessShortcutOutlined /> {t('workWithUs.benefits')}
              </Typography>
              <List>
                {benefits.map((benefit: string, index: number) => (
                  <ListItem key={index} className={classes.listItem}>
                    <div className={classes.bulletPoint} />
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
